import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {SignageRenderStateProps, UpdatePosterDataProps, UpdateShouldHideControlsProps} from '@Components/poster-editor/poster-editor-reducer';
import {updateShouldHideControls} from '@Components/poster-editor/poster-editor-reducer';
import {onPosterItemsModified, updateArePosterItemsMoving, updateArePosterItemsRotating, updateArePosterItemsScaling} from '@Components/poster-editor/poster-editor-reducer';
import {
  markHistoryNavigation,
  restoreInitialData,
  setMobilePageSelectionModeState,
  setAudioSelection,
  setSelectedTransitionBoundaryId,
  updateActiveItemsOnPage,
  updateAudioItemIsPlaying,
  updateCurrentUserId,
  updateDidPosterRecentlySave,
  updateDidPosterSaveFailed,
  updateFitToScreenOnWhiteboardResize,
  updateHoveredItem,
  updateIsTimelineModeOn,
  updateIsPosterInitialized,
  updateIsPosterSaving,
  updateHasRedo,
  updateHasUndo,
  updatePageData,
  updatePageIdToScrollToOnWhiteboardResize,
  updatePosterData,
  updateSignageRenderState,
  updatePosterScaling,
  updateSaveConflictDataDetails,
  updateSidebarState,
  updateSpellCheckSettingsState,
  updateTextSelectionStyles,
} from '@Components/poster-editor/poster-editor-reducer';
import {isPosterVideo} from '@Libraries/poster-object.library';
import {notifyItemTransformChanged, resetPosterSession, updatePagePlaybackTime} from '@Components/poster-editor/poster-session-reducer';
import {remoteSessionLeft, remoteSessionSeen, resetPosterPresence, staleRemoteSessionsPruned} from '@Components/poster-editor/poster-presence-reducer';
import type {PosterObject} from '@PosterWhiteboard/poster/poster.types';
import {stabilizePosterObject} from '@PosterWhiteboard/poster/stabilize-poster-object';
import type {TextSelectionStylesSummary} from '@PosterWhiteboard/items/text-item/text-style-runs.types';
import isEqual from 'lodash/isEqual';

export class PosterRedux {
  private readonly poster: Poster;
  #lastTextSelectionStyles: TextSelectionStylesSummary | null = null;

  constructor(poster: Poster) {
    this.poster = poster;
  }

  public onDispose(): void {
    window.PMW.redux.store.dispatch(restoreInitialData());
    window.PMW.redux.store.dispatch(resetPosterSession());
    window.PMW.redux.store.dispatch(resetPosterPresence());
  }

  public isAnyItemBeingTransformed(): boolean {
    const {arePosterItemsMoving, arePosterItemsScaling, arePosterItemsRotating} = window.PMW.redux.store.getState().posterEditor;
    return [arePosterItemsMoving, arePosterItemsScaling, arePosterItemsRotating].some((byPage) => Object.values(byPage).some(Boolean));
  }

  public remoteSessionSeen(payload: Parameters<typeof remoteSessionSeen>[0]): void {
    window.PMW.redux.store.dispatch(remoteSessionSeen(payload));
  }

  public remoteSessionLeft(payload: Parameters<typeof remoteSessionLeft>[0]): void {
    window.PMW.redux.store.dispatch(remoteSessionLeft(payload));
  }

  public pruneStaleRemoteSessions(now: number): void {
    window.PMW.redux.store.dispatch(staleRemoteSessionsPruned(now));
  }

  public resetPosterPresence(): void {
    window.PMW.redux.store.dispatch(resetPosterPresence());
  }

  public getRemoteSessionCount(): number {
    return Object.keys(window.PMW.redux.store.getState().posterPresence.remoteSessions).length;
  }

  public updateReduxData(): void {
    // Async work outlives the editor: a dominant-colour extraction scheduled on an idle callback can
    // resolve after dispose(), when the whiteboard DOM is gone (`toObject()` would throw reading the
    // container) and `onDispose()` has already restored the slice to its initial state.
    if (this.poster.isDisposed) {
      return;
    }
    // Every poster-data publish is the moment to re-establish the playhead invariant: a mutation may
    // have shortened the current page (audio drag/trim, item removal, ...), and `toObject()` below
    // serializes `currentTime`, so clamping here keeps the seek indicator and ruler from showing a
    // playhead past the new end. A no-op when the playhead is still in bounds.
    this.poster.getCurrentPage().reconcilePlayheadToDuration();
    const previous = window.PMW.redux.store.getState().posterEditor.posterObject ?? undefined;
    const posterObject = stabilizePosterObject(previous, this.poster.toObject());
    this.poster.pages.rememberCommittedObjects(posterObject.pages);
    window.PMW.redux.store.dispatch(updatePosterData(this.getPosterDataForRedux(posterObject)));
  }

  public updatePosterScaling(): void {
    window.PMW.redux.store.dispatch(updatePosterScaling(this.poster.scaling.toObject()));
  }

  /** A move/scale/rotate tick: overlays re-measure the live Fabric object; the poster mirror is untouched until the commit. */
  public notifyItemTransformChanged(pageHashId: string): void {
    window.PMW.redux.store.dispatch(notifyItemTransformChanged(pageHashId));
  }

  public updatePagePlaybackTime(pageHashId: string, currentTime: number): void {
    window.PMW.redux.store.dispatch(updatePagePlaybackTime({pageHashId, currentTime}));
  }

  public updateHasUndoInRedux(): void {
    window.PMW.redux.store.dispatch(updateHasUndo(this.poster.history.hasUndo()));
  }

  /**
   * Publishes the active text-editing selection's effective styles. Selection events fire per
   * keystroke and caret move, so identical summaries are swallowed here instead of churning the
   * store.
   */
  public updateTextSelectionStyles(summary: TextSelectionStylesSummary | null): void {
    if (isEqual(summary, this.#lastTextSelectionStyles)) {
      return;
    }
    this.#lastTextSelectionStyles = summary;
    window.PMW.redux.store.dispatch(updateTextSelectionStyles(summary));
  }

  public clearTextSelectionStyles(): void {
    this.updateTextSelectionStyles(null);
  }

  public updatePageData(payload: Parameters<typeof updatePageData>[0]): void {
    window.PMW.redux.store.dispatch(updatePageData(payload));
  }

  public updateCurrentUserId(userId: Parameters<typeof updateCurrentUserId>[0]): void {
    window.PMW.redux.store.dispatch(updateCurrentUserId(userId));
  }

  public updateIsPosterInitialized(val: Parameters<typeof updateIsPosterInitialized>[0]): void {
    window.PMW.redux.store.dispatch(updateIsPosterInitialized(val));
  }

  public updateIsTimelineModeOn(payload: Parameters<typeof updateIsTimelineModeOn>[0]): void {
    window.PMW.redux.store.dispatch(updateIsTimelineModeOn(payload));
  }

  public updateFitToScreenOnWhiteboardResize(val: Parameters<typeof updateFitToScreenOnWhiteboardResize>[0]): void {
    window.PMW.redux.store.dispatch(updateFitToScreenOnWhiteboardResize(val));
  }

  public updatePageIdToScrollToOnWhiteboardResize(pageId: Parameters<typeof updatePageIdToScrollToOnWhiteboardResize>[0]): void {
    window.PMW.redux.store.dispatch(updatePageIdToScrollToOnWhiteboardResize(pageId));
  }

  public updateSaveConflictDataDetails(payload: Parameters<typeof updateSaveConflictDataDetails>[0]): void {
    window.PMW.redux.store.dispatch(updateSaveConflictDataDetails(payload));
  }

  public setMobilePageSelectionModeState(state: Parameters<typeof setMobilePageSelectionModeState>[0]): void {
    window.PMW.redux.store.dispatch(setMobilePageSelectionModeState(state));
  }

  public updateActiveItemsOnPage(payload: Parameters<typeof updateActiveItemsOnPage>[0]): void {
    window.PMW.redux.store.dispatch(updateActiveItemsOnPage(payload));
  }

  public updateHoveredItem(payload: Parameters<typeof updateHoveredItem>[0]): void {
    window.PMW.redux.store.dispatch(updateHoveredItem(payload));
  }

  public updateSidebarState(isOpen: boolean): void {
    window.PMW.redux.store.dispatch(updateSidebarState(isOpen));
  }

  public updateSpellCheckSettingsState(isOpen: boolean): void {
    window.PMW.redux.store.dispatch(updateSpellCheckSettingsState(isOpen));
  }

  public updateDidPosterSaveFailed(val: boolean): void {
    window.PMW.redux.store.dispatch(updateDidPosterSaveFailed(val));
  }

  public setPosterItemsModificationState(modificationType: 'scaling' | 'moving' | 'rotating' | false, pageHashedId: string): void {
    const posterEditorState = window.PMW.redux.store.getState().posterEditor;

    switch (modificationType) {
      case 'moving':
        if (!posterEditorState.arePosterItemsMoving[pageHashedId]) {
          window.PMW.redux.store.dispatch(updateArePosterItemsMoving({pageHashedId: pageHashedId, isModifying: true}));
        }
        return;
      case 'scaling':
        if (!posterEditorState.arePosterItemsScaling[pageHashedId]) {
          window.PMW.redux.store.dispatch(updateArePosterItemsScaling({pageHashedId: pageHashedId, isModifying: true}));
        }
        return;

      case 'rotating':
        if (!posterEditorState.arePosterItemsRotating[pageHashedId]) {
          window.PMW.redux.store.dispatch(
            updateArePosterItemsRotating({
              pageHashedId: pageHashedId,
              isModifying: true,
            })
          );
        }
        return;

      case false:
        window.PMW.redux.store.dispatch(onPosterItemsModified(pageHashedId));
        return;
    }
  }

  public setActiveItemControlsHideState = (updateShouldHideControlsProps: UpdateShouldHideControlsProps): void => {
    window.PMW.redux.store.dispatch(updateShouldHideControls(updateShouldHideControlsProps));
  };

  public areAnyPosterItemsModifying(): boolean {
    const posterEditorState = window.PMW.redux.store.getState().posterEditor;
    // eslint-disable-next-line no-restricted-syntax
    for (const _ in posterEditorState.arePosterItemsMoving) {
      return true;
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const _ in posterEditorState.arePosterItemsScaling) {
      return true;
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const _ in posterEditorState.arePosterItemsRotating) {
      return true;
    }

    return false;
  }

  public updateIsPosterSaving(val: boolean): void {
    window.PMW.redux.store.dispatch(updateIsPosterSaving(val));
  }

  public updateDidPosterRecentlySave(val: boolean): void {
    window.PMW.redux.store.dispatch(updateDidPosterRecentlySave(val));
  }

  public updateSignageRenderState(signageRenderState: SignageRenderStateProps): void {
    window.PMW.redux.store.dispatch(updateSignageRenderState(signageRenderState));
  }

  public updateHasRedoInRedux(): void {
    window.PMW.redux.store.dispatch(updateHasRedo(this.poster.history.hasRedo()));
  }

  public updateAudioItemIsPlayingInRedux(audioItemUID: string, isPlaying: boolean): void {
    window.PMW.redux.store.dispatch(
      updateAudioItemIsPlaying({
        audioItemUID,
        isPlaying,
      })
    );
  }

  public updateAudioSelection(): void {
    window.PMW.redux.store.dispatch(setAudioSelection(this.poster.getCurrentPage().audioClips.getSelectedAudioItemUIDs()));
  }

  public updateSelectedTransitionBoundaryId(): void {
    window.PMW.redux.store.dispatch(setSelectedTransitionBoundaryId(this.poster.getCurrentPage().transitions.selectedBoundaryId));
  }

  public updateUndoRedoStates(): void {
    this.updateHasUndoInRedux();
    this.updateHasRedoInRedux();
  }

  public markHistoryNavigation(): void {
    window.PMW.redux.store.dispatch(markHistoryNavigation());
  }

  private getPosterDataForRedux(posterObject: PosterObject): UpdatePosterDataProps {
    const isVideo = isPosterVideo(posterObject);
    return {
      isVideo,
      posterObject,
      currentPageId: this.poster.getCurrentPage().hashedID,
      isPosterVideo: isVideo,
      hasUnsavedChanges: this.poster.savePoster.hasUnsavedChanges(),
      hasSavedSinceLoad: this.poster.savePoster.getHasSavedSinceLoad(),
    };
  }
}
